import {
  ArrowLeftIcon,
  BookOpenIcon,
  CheckIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import type {
  Chapter,
  Source,
  Title,
  TitleSource,
} from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

interface ChapterWithSource extends Chapter {
  sources: Source
}

interface TitleSourceWithSource extends TitleSource {
  sources: Source
}

function TitleDetailContent() {
  const { id } = useParams<{ id: string }>()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  const [title, setTitle] = useState<Title | null>(null)
  const [titleSources, setTitleSources] = useState<TitleSourceWithSource[]>([])
  const [chapters, setChapters] = useState<ChapterWithSource[]>([])
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null)
  const [isFollowing, setIsFollowing] = useState(false)
  const [progressChapterId, setProgressChapterId] = useState<string | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(true)
  const [followLoading, setFollowLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    const resolvedId = id

    async function load() {
      setIsLoading(true)

      const [titleRes, tsRes, followRes, progressRes] = await Promise.all([
        supabase.from('titles').select('*').eq('id', resolvedId).single(),
        supabase
          .from('title_sources')
          .select('*, sources(*)')
          .eq('title_id', resolvedId),
        supabase
          .from('follows')
          .select('title_id')
          .eq('title_id', resolvedId)
          .maybeSingle(),
        supabase
          .from('progress')
          .select('chapter_id')
          .eq('title_id', resolvedId)
          .maybeSingle(),
      ])

      setTitle(titleRes.data)
      const ts = (tsRes.data ?? []) as TitleSourceWithSource[]
      setTitleSources(ts)
      if (ts.length) setActiveSourceId(ts[0].source_id)
      setIsFollowing(!!followRes.data)
      setProgressChapterId(progressRes.data?.chapter_id ?? null)
      setIsLoading(false)
    }

    load()
  }, [id])

  useEffect(() => {
    if (!id || !activeSourceId) return
    const resolvedId = id
    const resolvedSourceId = activeSourceId

    async function loadChapters() {
      const { data } = await supabase
        .from('chapters')
        .select('*, sources(*)')
        .eq('title_id', resolvedId)
        .eq('source_id', resolvedSourceId)
        .order('chapter_no', { ascending: false })

      setChapters((data ?? []) as ChapterWithSource[])
    }

    loadChapters()
  }, [id, activeSourceId])

  async function toggleFollow() {
    if (!user || !id) return
    setFollowLoading(true)
    if (isFollowing) {
      await supabase.from('follows').delete().eq('title_id', id)
      setIsFollowing(false)
    } else {
      await supabase.from('follows').insert({ user_id: user.id, title_id: id })
      setIsFollowing(true)
    }
    setFollowLoading(false)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  if (!title) {
    return (
      <div className="py-20 text-center text-gray-500">
        <p>Title not found.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:underline"
        >
          Go back
        </button>
      </div>
    )
  }

  const TYPE_BADGE: Record<Title['type'], string> = {
    manga: 'bg-red-100 text-red-700',
    manhwa: 'bg-blue-100 text-blue-700',
    manhua: 'bg-yellow-100 text-yellow-700',
    webtoon: 'bg-purple-100 text-purple-700',
  }

  const STATUS_BADGE: Record<Title['status'], string> = {
    ongoing: 'bg-green-100 text-green-700',
    completed: 'bg-gray-100 text-gray-600',
    hiatus: 'bg-orange-100 text-orange-700',
  }

  return (
    <div className="mx-auto max-w-4xl">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-gray-500 text-sm hover:text-gray-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back
      </button>

      {/* Header */}
      <div className="mb-8 flex gap-6">
        <div className="h-44 w-32 shrink-0 overflow-hidden rounded-xl bg-gray-100 shadow">
          {title.cover_url ? (
            <img
              src={title.cover_url}
              alt={title.canonical_name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <BookOpenIcon className="h-10 w-10 text-gray-300" />
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <h1 className="font-bold text-2xl text-gray-900">
            {title.canonical_name}
          </h1>
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium text-xs capitalize ${TYPE_BADGE[title.type]}`}
            >
              {title.type}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium text-xs capitalize ${STATUS_BADGE[title.status]}`}
            >
              {title.status}
            </span>
          </div>
          {title.description && (
            <p className="line-clamp-4 text-gray-600 text-sm">
              {title.description}
            </p>
          )}

          <div className="mt-auto flex flex-wrap gap-2">
            <button
              type="button"
              disabled={followLoading}
              onClick={toggleFollow}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium text-sm transition-colors ${
                isFollowing
                  ? 'bg-green-50 text-green-700 ring-1 ring-green-300 hover:bg-red-50 hover:text-red-600 hover:ring-red-300'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isFollowing ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Following
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  Follow
                </>
              )}
            </button>

            {progressChapterId && (
              <Link
                to={`/read/${progressChapterId}`}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 font-medium text-sm text-white hover:bg-gray-900"
              >
                <BookOpenIcon className="h-4 w-4" />
                Continue reading
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Source tabs */}
      {titleSources.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {titleSources.map((ts) => (
            <button
              key={ts.source_id}
              type="button"
              onClick={() => setActiveSourceId(ts.source_id)}
              className={`rounded-lg px-4 py-1.5 font-medium text-sm transition-colors ${
                activeSourceId === ts.source_id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {ts.sources.name}
            </button>
          ))}
        </div>
      )}

      {/* Chapters list */}
      {chapters.length === 0 ? (
        <p className="py-8 text-center text-gray-400 text-sm">
          No chapters available for this source.
        </p>
      ) : (
        <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {chapters.map((ch) => (
            <div
              key={ch.id}
              className={`flex items-center justify-between px-4 py-3 ${
                ch.id === progressChapterId ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                {ch.id === progressChapterId && (
                  <span
                    className="h-2 w-2 rounded-full bg-blue-500"
                    title="Last read"
                  />
                )}
                <div>
                  <span className="font-medium text-gray-800 text-sm">
                    Chapter {ch.chapter_no}
                  </span>
                  {ch.title && (
                    <span className="ml-2 text-gray-500 text-sm">
                      {ch.title}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/read/${ch.id}`}
                className="rounded-lg bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
              >
                Read
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function TitleDetailPage() {
  return (
    <UserGuard>
      <div className="px-4 py-8">
        <TitleDetailContent />
      </div>
    </UserGuard>
  )
}
